import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useSnack } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'underscore';
import { useIsFetching } from 'react-query';

import { SoknadSteg } from '../../../components/registering/utils/registerEnums';
import { useUser } from '../../../hooks/useUser';
import useStore from '../../../hooks/useStore';
import { RegistrationCommonStep, RegistrationStep } from '../../../utils/navigation';
import { getHasErrors, NumberedFieldErrorMap } from '../../../components/registering/utils/validation';
import { updateRegistration as updateRegistrationAction } from '../../../store/registration/actions';
import { Detaljeringssteg, UserInput } from '../../../types/registration/userInput';
import { getDetailingSteps } from '../../../store/registration/selectors';
import { SessionExpiresDialog } from '../../../components/sessionExpiresDialog';
import { useTiltakEnergiPlan } from '../../../hooks/useTiltakEnergiplan';
import { useEnergyAnalysis } from '../../../hooks/useEnergyAnalysis';
import { queryKeys } from '../../../utils/react-query';
import { createErrorMessage } from '../../../utils/utils';
import { BackendErrorInformation } from '../components/backendErrorInformation';
import { Energimerkeversjon } from '../../../types/energimerke';

import RegistrationFormContext, { RegistrationFormValues } from './context';
import { useUpdateRegistration } from './useUpdateRegistration';
import { getSteps } from './utils';
import useStepErrorInformation from './useStepErrorInformation';
import { useUpdateParameters } from './useUpdateParameters';
import { useStepFromURL } from './useStepFromURL';

/**
 * For a registration, regId and registrationId will always be the same. When creating a soknad, regId and
 * registrationId will be the same for steg1. For steg2/steg3 regId is the registrationId of the previous
 * step and registrationId is undefined. After steg2/steg3 is created regId and registrationId continues to
 * mirror each other. All of this can be discarded if we start to create a registration/soknad before
 * navigating to the Registration page.
 */
type RegistrationFormProviderProps = {
  /**
   * ID of the current registration
   */
  registrationId?: string;

  soknad?: {
    soknadSteg: SoknadSteg;
    soknadId?: string;
  };
  children: ReactNode;
  energimerkeversjon?: Energimerkeversjon;
};

const RegistrationFormProvider: FC<RegistrationFormProviderProps> = ({
  children,
  registrationId,
  soknad,
  energimerkeversjon
}) => {
  // External hooks
  const { addSnack } = useSnack();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Energimerkeversjon.V2025 use static routes per step. Energimerkeversjon.V2023 uses dynamic routing with step param
  // This provider is needed for V2025 until all steps are migrated to V2025, therefore we need to handle both routing
  // types
  const { step } = useStepFromURL(energimerkeversjon);

  // Internal hooks
  const { user } = useUser();

  // API requests
  const { energyAnalysis } = useEnergyAnalysis(registrationId);
  const {
    isLoading: tiltakLoading,
    tiltakList,
    energiplanId
  } = useTiltakEnergiPlan(energyAnalysis?.energiplan?.energiplanId);

  const { updateRegistration, updateIsLoading } = useUpdateRegistration();

  const { updateParameters, updateParametersIsLoading } = useUpdateParameters(registrationId);

  // Used to check if useSubmitRegistrationQuery is submitting. Is used in summary page.
  // Will be removed in the future
  const registrationIsSubmitting = useIsFetching([queryKeys.UNSAFE_SumitRegistrationAsQuery]);

  // Redux stuff
  const detailingSteps = useSelector(getDetailingSteps, _.isEqual);
  const dispatch = useDispatch();
  const store = useStore();

  // Errors
  const getStepErrorInformation = useStepErrorInformation();
  const [displayErrors, setDisplayErrors] = useState(false);
  const [errorInformation, setErrorInformation] = useState<string[]>();

  // Handle errors when adding a new door or window
  const [startedObjectErrors, setStartedObjectErrors] = useState<NumberedFieldErrorMap>({});

  // Step

  // In order to make step navigation work with legacy steps in V2025 flow without breaking V2023 flow, we update
  // getSteps to return steps based on energimerkeversjon. In the future we remove the use of step logic from this
  // provider in the V2025 flow.
  const [steps, setSteps] = useState<RegistrationStep[]>(() =>
    getSteps(user, detailingSteps, soknad?.soknadSteg, energimerkeversjon)
  );
  const updateSteps = (ds?: Detaljeringssteg) => setSteps(getSteps(user, ds, soknad?.soknadSteg, energimerkeversjon));

  useEffect(() => {
    updateSteps(detailingSteps);
  }, [detailingSteps]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const nextStep = step ? steps[steps.indexOf(step) + 1] : undefined;
  const prevStep =
    step && step !== RegistrationCommonStep.ENERGY_CERTIFICATE ? steps[steps.indexOf(step) - 1] : undefined;

  const updateUserInput = (userInput?: UserInput) => {
    dispatch(updateRegistrationAction({ userInput }));
    addSnack(t('registerScreen.saveRegistration.saveSucceeded'));
  };

  const showErrorMessage = (context?: 'publish') => {
    addSnack(
      createErrorMessage(
        t('registerScreen.saveRegistration.saveFailed', {
          context
        })
      ),
      { variant: 'error' }
    );
  };

  const confirmNavigation = () => {
    if (nextStep) {
      navigateToStep(nextStep);
      setErrorInformation([]);
    }
  };

  const navigateToStep = (newStep: RegistrationStep | undefined) => {
    if (!newStep) {
      return;
    }

    // URL structure is different for V2025 and V2023
    if (energimerkeversjon === Energimerkeversjon.V2025) {
      navigate(`${newStep}`);
      return;
    }

    navigate(`../../${newStep}`);
  };

  // TODO: Move navigation to each step page to make flow easier to understand.
  const goToNextStep = () => {
    if (getHasErrors(store.getState(), step) || Object.keys(startedObjectErrors).length > 0) {
      setDisplayErrors(true);

      return;
    }

    setDisplayErrors(false);

    if (!nextStep) {
      return;
    }
    const updateRegistrationInput = {
      registrationId,
      userInput: store.getState().register.userInput
    };

    updateRegistration(updateRegistrationInput, {
      onSuccess: (data) => {
        const userInput = data?.userInput;
        const userInputErrorInformation = data?.userInputErrorInformation;

        const newErrorInformation = getStepErrorInformation(step, userInputErrorInformation);

        setErrorInformation(newErrorInformation);
        updateUserInput(userInput);

        if ((newErrorInformation?.length ?? 0) === 0) {
          navigateToStep(nextStep);
        }
      },
      onError: () => showErrorMessage()
    });
  };

  const goToPrevStep = () => {
    setDisplayErrors(false);
    // If we start to add a new door/window but don't want to finish, we should be able to
    // navigate to a previous step without errors about not having saved the door/window.
    // This will only affect navigating backwards. Forwards we will still validate
    // before navigating.
    setStartedObjectErrors({});

    if (!prevStep) {
      return;
    }
    // Navigate back and try to save a snapshot of the registration.
    const updateRegistrationInput = {
      registrationId,
      userInput: store.getState().register.userInput
    };

    updateRegistration(updateRegistrationInput, {
      onSettled: () => navigateToStep(prevStep)
    });
  };

  const goToStep = (registrationStep: RegistrationStep) => {
    if (registrationStep === RegistrationCommonStep.PARAMETERS) {
      // Navigate and try to save a snapshot of the parameters.
      updateParameters(undefined, {
        onSettled: () => navigateToStep(registrationStep)
      });
    } else {
      // Navigate and try to save a snapshot of the registration.
      const updateRegistrationInput = {
        registrationId,
        userInput: store.getState().register.userInput
      };

      updateRegistration(updateRegistrationInput, {
        onSettled: () => navigateToStep(registrationStep)
      });
    }
  };

  const values: RegistrationFormValues = {
    confirmNavigation,
    displayErrors,
    startedObjectErrors,
    setStartedObjectErrors,
    energiplanId,
    errorInformation,
    goToStep,
    goToNextStep,
    goToPrevStep,
    loadingStep: updateIsLoading || updateParametersIsLoading || registrationIsSubmitting > 0,
    nextStep,
    prevStep,
    registrationId,
    registrationIsPublished: !!energyAnalysis?.registrering.publisertDato,
    showErrorMessage,
    soknadSteg: soknad?.soknadSteg,
    step,
    steps,
    tiltakList,
    tiltakLoading,
    updateSteps,
    attestUrl: energyAnalysis?.energiplan?.attestUri
  };

  return (
    <RegistrationFormContext.Provider value={values}>
      {children}
      <BackendErrorInformation
        confirmNavigation={confirmNavigation}
        errorInformation={errorInformation ?? []}
        step={step}
      />
      <SessionExpiresDialog params={{ registrationId, step }} />
    </RegistrationFormContext.Provider>
  );
};

export default RegistrationFormProvider;
